import * as React from 'react';
import { Dispatch, SetStateAction } from 'react';
import { Button as MuiButton } from '@material-ui/core';
import { DateInterval } from 'types/plansales/billing';
import { getDateDifference, getUtcDateDash } from 'modules/utils/time';
import styled from 'styled-components';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import DatePicker from 'components/common/searchfilter/contents/DatePicker';
import { ButtonObject, SearchDateTerms } from 'types/common/searchfilter';
import { spacing } from '@material-ui/system';
import {
  getRangePickerMappingStartDate,
  getRangePickerMappingDates,
  subscriptionTerms,
} from 'constants/backorder/rangePickerMapperDates';

interface Props {
  dateInterval: string[];
  setDateInterval: Dispatch<SetStateAction<[string, string]>>;
  shortcutButtons?: ButtonObject[];
  disableOnFrom?: boolean;
  disableOnTo?: boolean;
  fromMinDate?: string;
  toMinDate?: string;
  selectedShortcut: SearchDateTerms | null;
  setSelectedShortcut: Dispatch<SetStateAction<SearchDateTerms | null>>;
}

const SubscriptionRangePicker = ({
  dateInterval,
  setDateInterval,
  shortcutButtons,
  disableOnFrom,
  disableOnTo,
  fromMinDate,
  toMinDate,
  selectedShortcut,
  setSelectedShortcut,
}: Props) => {
  const [fromDate, toDate] = dateInterval;

  const getMappedEndDateByShortcut = (startDate: string): string => {
    if (selectedShortcut === SearchDateTerms['1_MONTH']) {
      return getRangePickerMappingDates({
        startDate,
        subscriptionTerms: subscriptionTerms[SearchDateTerms['1_MONTH']],
      });
    }
    if (selectedShortcut === SearchDateTerms['3_MONTHS']) {
      return getRangePickerMappingDates({
        startDate,
        subscriptionTerms: subscriptionTerms[SearchDateTerms['3_MONTHS']],
      });
    }
    if (selectedShortcut === SearchDateTerms['1_YEAR']) {
      return getRangePickerMappingDates({
        startDate,
        subscriptionTerms: subscriptionTerms[SearchDateTerms['1_YEAR']],
      });
    }
    if (selectedShortcut === SearchDateTerms['UNTIL_2099']) {
      return '2099-12-31';
    }
    return getRangePickerMappingDates({
      startDate,
      subscriptionTerms: subscriptionTerms[SearchDateTerms['1_MONTH']],
    });
  };
  const getMappedStartDateByShortcut = (endDate: string): string => {
    if (selectedShortcut === SearchDateTerms['1_MONTH']) {
      return getRangePickerMappingStartDate({
        endDate,
        subscriptionTerms: subscriptionTerms[SearchDateTerms['1_MONTH']],
      });
    }
    if (selectedShortcut === SearchDateTerms['3_MONTHS']) {
      return getRangePickerMappingStartDate({
        endDate,
        subscriptionTerms: subscriptionTerms[SearchDateTerms['3_MONTHS']],
      });
    }
    if (selectedShortcut === SearchDateTerms['1_YEAR']) {
      return getRangePickerMappingStartDate({
        endDate,
        subscriptionTerms: subscriptionTerms[SearchDateTerms['1_YEAR']],
      });
    }
    return getRangePickerMappingStartDate({
      endDate,
      subscriptionTerms: subscriptionTerms[SearchDateTerms['1_MONTH']],
    });
  };

  const onChangeDate = (type: DateInterval) => (newDateValue: MaterialUiPickersDate) => {
    const newDateDashFormat = getUtcDateDash(newDateValue);

    if (type === DateInterval.FROM) {
      const shouldUpdateEndDateByShortcut =
        selectedShortcut === SearchDateTerms['1_MONTH'] ||
        selectedShortcut === SearchDateTerms['3_MONTHS'] ||
        selectedShortcut === SearchDateTerms['1_YEAR'] ||
        selectedShortcut === SearchDateTerms['UNTIL_2099'];
      const shouldUpdateEndDateByDefaultRule = getDateDifference(newDateDashFormat, toDate) < 0;
      const newDateTo = shouldUpdateEndDateByShortcut
        ? getMappedEndDateByShortcut(newDateDashFormat)
        : shouldUpdateEndDateByDefaultRule
          ? getRangePickerMappingDates({
              startDate: newDateDashFormat,
              subscriptionTerms: subscriptionTerms[SearchDateTerms['1_MONTH']],
            })
          : toDate;

      setDateInterval([newDateDashFormat, newDateTo]);
      return;
    }

    const shouldUpdateStartDateByShortcut =
      selectedShortcut === SearchDateTerms['1_MONTH'] ||
      selectedShortcut === SearchDateTerms['3_MONTHS'] ||
      selectedShortcut === SearchDateTerms['1_YEAR'];
    const shouldUpdateStartDateByDefaultRule = getDateDifference(fromDate, newDateDashFormat) < 0;
    const newDateFrom =
      shouldUpdateStartDateByShortcut || shouldUpdateStartDateByDefaultRule
        ? getMappedStartDateByShortcut(newDateDashFormat)
        : fromDate;

    setDateInterval([newDateFrom, newDateDashFormat]);
    if (selectedShortcut === SearchDateTerms['UNTIL_2099']) {
      setSelectedShortcut(null);
    }
  };

  return (
    <>
      <DateFiltersBlock>
        <DatePickerSection>
          <DatePickerInput>
            <DatePicker
              value={fromDate}
              onChange={onChangeDate(DateInterval.FROM)}
              disabled={disableOnFrom}
              minDate={fromMinDate}
            />
          </DatePickerInput>
          <Tilde>~</Tilde>
          <DatePickerInput>
            <DatePicker
              value={toDate}
              onChange={onChangeDate(DateInterval.TO)}
              disabled={disableOnTo}
              minDate={toMinDate}
            />
          </DatePickerInput>
        </DatePickerSection>
        {shortcutButtons && (
          <ButtonSection>
            {shortcutButtons.map((button: ButtonObject, index: number) => (
              <Button
                key={index}
                variant="contained"
                color={button.selected ? 'secondary' : 'default'}
                size="small"
                onClick={button.onClick}
                disabled={button.disabled}
                mb={2}
              >
                {button.name}
              </Button>
            ))}
          </ButtonSection>
        )}
      </DateFiltersBlock>
    </>
  );
};

export default SubscriptionRangePicker;

const Button = styled(MuiButton)(spacing);

const Tilde = styled.div`
  font-size: 18px;
  margin: 0 8px;
`;

const ButtonSection = styled.div`
  & > button {
    margin-left: ${props => props.theme.spacing(3)}px;
  }
`;

export const DatePickerInput = styled.div`
  width: 150px;
  input {
    padding-left: 2px;
  }
`;

const DatePickerSection = styled.div`
  display: flex;
  flex-direction: row;
`;

const DateFiltersBlock = styled.div`
  display: flex;
  align-items: center;
`;
